import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import {
  LayoutDashboard,
  Building2,
  Users,
  Shield,
  FileText,
  Bell,
  CheckSquare,
  User,
  Network,
  Database,
  UsersRound,
  Clock,
  Wallet,
  CalendarDays,
  Calculator,
  Receipt,
  AlertTriangle,
  ClipboardCheck,
  FileBarChart,
  Banknote,
  ArrowLeftRight,
  TreePalm,
  ShieldCheck,
  UserCheck,
  FileSearch,
  TrendingUp,
  Wrench,
  FileSignature,
  BookOpen,
  Package,
  Gauge,
  Home,
  Calendar,
  DollarSign,
  HelpCircle,
  Inbox,
  FileBarChart2,
  BarChart3,
  Landmark,
  PieChart,
  CreditCard,
  Layers,
  Plane,
} from 'lucide-react';
import { Sparkles } from 'lucide-react';
import { ClipboardList } from 'lucide-react';
import { MapPin } from 'lucide-react';
import { Coins } from 'lucide-react';
import { ScanFace } from 'lucide-react';
import { History } from 'lucide-react';
import { ListChecks } from 'lucide-react';
import { LifeBuoy, Trash2, FileCheck } from 'lucide-react';
import { canViewPath } from '../constants/permissions';

const SidebarItem = ({ to, label, icon: Icon, active, onClick, isChild, shimmerStyle, goldTextActive, goldText, isOpen }) => {
  return (
    <Link
      to={to}
      onClick={onClick}
      data-testid={`sidebar-${label.toLowerCase().replace(/[& ]/g, '-')}`}
      style={{
        background: active ? 'rgba(255, 192, 0, 0.15)' : 'transparent',
        boxShadow: active ? 'inset 0 0 24px rgba(255, 192, 0, 0.10)' : 'none',
      }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'rgba(255, 192, 0, 0.07)'; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
      className={`w-full flex items-center h-12 transition-all duration-200 border-l-4 rounded-md select-none gap-3 shrink-0
        ${isChild ? 'pl-[36px] md:pl-0 lg:pl-[36px] pr-3' : 'px-3'}
        ${active ? 'border-amber-400' : 'border-transparent'}
        md:px-0 md:justify-center lg:px-3 lg:justify-start`}
    >
      <div className="flex items-center justify-center shrink-0 w-5 h-5 md:mx-auto lg:mx-0">
        <Icon className="w-5 h-5" strokeWidth={1.7} style={{ color: active ? '#FFE76A' : '#FFC000', filter: active ? 'drop-shadow(0 0 4px rgba(255, 192, 0, 0.5))' : 'none' }} />
      </div>
      <span
        className={`font-medium md:hidden lg:inline whitespace-nowrap ${isChild ? 'text-xs' : 'text-sm'} ${isOpen ? 'inline' : ''}`}
        style={shimmerStyle(active ? goldTextActive : (isChild ? 'linear-gradient(135deg, #D4AF37 0%, #FFC000 60%, #FFE76A 100%)' : goldText), active)}
      >
        {label}
      </span>
    </Link>
  );
};

const SidebarGroup = ({ label, icon: Icon, parentActive, isExpanded, onExpandClick, children, shimmerStyle, goldTextActive, goldText, isOpen }) => {
  return (
    <div className="w-full shrink-0">
      <button
        data-testid={`sidebar-${label.toLowerCase().replace(/[& ]/g, '-')}`}
        onClick={onExpandClick}
        style={{
          background: parentActive ? 'rgba(255, 192, 0, 0.12)' : 'transparent',
          boxShadow: parentActive ? 'inset 0 0 24px rgba(255, 192, 0, 0.10)' : 'none',
        }}
        onMouseEnter={(e) => { if (!parentActive) e.currentTarget.style.background = 'rgba(255, 192, 0, 0.07)'; }}
        onMouseLeave={(e) => { if (!parentActive) e.currentTarget.style.background = 'transparent'; }}
        className={`group w-full flex items-center justify-between h-12 px-3 transition-all duration-200 border-l-4 rounded-md select-none
          ${parentActive ? 'border-amber-400' : 'border-transparent'}
          md:px-0 md:justify-center lg:px-3 lg:justify-between`}
      >
        <span className="flex items-center gap-3 md:w-full md:justify-center lg:justify-start">
          <div className="flex items-center justify-center shrink-0 w-5 h-5 md:mx-auto lg:mx-0">
            <Icon className="w-5 h-5" strokeWidth={1.7} style={{ color: parentActive ? '#FFE76A' : '#FFC000', filter: parentActive ? 'drop-shadow(0 0 4px rgba(255, 192, 0, 0.5))' : 'none' }} />
          </div>
          <span
            className={`text-sm font-semibold md:hidden lg:inline whitespace-nowrap ${isOpen ? 'inline' : ''}`}
            style={shimmerStyle(parentActive ? goldTextActive : goldText, parentActive)}
          >
            {label}
          </span>
        </span>
        <svg
          className={`w-4 h-4 transition-transform md:hidden lg:block shrink-0 ${isExpanded ? 'rotate-90' : ''} ${isOpen ? 'block' : ''}`}
          fill="none"
          stroke={parentActive ? '#FFE76A' : '#D4AF37'}
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
      {isExpanded && (
        <div 
          style={{ background: 'rgba(0, 0, 0, 0.25)', borderTop: '1px solid rgba(255, 192, 0, 0.12)', borderBottom: '1px solid rgba(255, 192, 0, 0.12)' }}
          className="w-full flex flex-col mt-0.5 space-y-0.5"
        >
          {children}
        </div>
      )}
    </div>
  );
};

const SidebarSection = ({ label, isOpen }) => {
  return (
    <div 
      className={`px-3 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wider md:hidden lg:block shrink-0 ${isOpen ? 'block' : ''}`}
      style={{ color: 'rgba(212, 175, 55, 0.6)', letterSpacing: '0.12em' }}
    >
      {label.replace(/^── /, '')}
    </div>
  );
};

const SidebarFooter = ({ user, shimmerStyle, goldText, isOpen, UserIcon }) => {
  return (
    <div className="p-4 border-t flex flex-col md:items-center lg:items-start shrink-0 w-full" style={{ borderColor: 'rgba(255, 192, 0, 0.22)' }}>
      <div className="hidden md:flex lg:hidden justify-center py-2 w-full">
        <UserIcon className="w-5 h-5 text-amber-500" />
      </div>
      <div className={`text-xs md:hidden lg:block ${isOpen ? 'block' : ''} w-full`}>
        <p style={{ color: '#D4AF37' }}>Logged in as</p>
        <p
          className="font-bold mt-1 text-sm truncate"
          style={shimmerStyle(goldText, true)}
          title={user?.name}
        >
          {user?.name}
        </p>
        <p className="capitalize font-medium text-xs truncate" style={shimmerStyle('linear-gradient(135deg, #D4AF37, #FFC000)', false)}>
          {user?.role?.replace('_', ' ')}
        </p>
      </div>
    </div>
  );
};

const Sidebar = ({ isOpen, onClose }) => {
  const { user, isReportingManager, activeView } = useAuth();
  const branding = useBranding();
  const location = useLocation();
  // Persist expansion across navigations so the user's manually-expanded group
  // doesn't collapse every time they click an unrelated top-level item.
  const [expandedMenu, setExpandedMenu] = React.useState(() => sessionStorage.getItem('sidebar-expanded') || null);
  const navRef = React.useRef(null);

  // Persist and restore sidebar scroll position across route changes
  // (Sidebar remounts with each page, which would otherwise reset scroll to 0)
  React.useLayoutEffect(() => {
    const saved = sessionStorage.getItem('sidebar-scroll');
    if (navRef.current && saved) {
      navRef.current.scrollTop = parseInt(saved, 10) || 0;
    }
    const nav = navRef.current;
    if (!nav) return;
    const onScroll = () => {
      sessionStorage.setItem('sidebar-scroll', String(nav.scrollTop));
    };
    nav.addEventListener('scroll', onScroll, { passive: true });
    return () => nav.removeEventListener('scroll', onScroll);
  }, []);

  const handleExpand = (label) => {
    const next = expandedMenu === label ? null : label;
    setExpandedMenu(next);
    if (next) sessionStorage.setItem('sidebar-expanded', next);
    else sessionStorage.removeItem('sidebar-expanded');
  };

  // ============================================================
  // Menu structure — Super Admin gets a PLATFORM-only menu.
  // All tenant/business roles get the full operations menu.
  // Super Admin is deliberately excluded from business modules.
  // ============================================================

  const isSuperAdmin = user?.role === 'super_admin';

  // Super Admin platform menu — tenant/users/modules management + audits only.
  const platformMenu = [
    { label: 'Platform Console', icon: LayoutDashboard, path: '/dashboard' },
    { label: 'Tenants', icon: Building2, path: '/tenants' },
    { label: 'Module Control', icon: Shield, path: '/module-control' },
    { label: 'Audit Logs', icon: FileText, path: '/audit-logs' },
    { label: 'Notifications', icon: Bell, path: '/notifications' },
    { label: 'Profile', icon: User, path: '/profile' },
  ];

  // Tenant/business menu — all operational modules. Super Admin NEVER in roles.
  const tenantMenu = [
    { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin', 'attendance_admin', 'finance_reviewer', 'department_manager', 'employee'] },
    { label: 'My Portal', icon: Home, path: '/ess',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin', 'attendance_admin', 'finance_reviewer', 'department_manager', 'employee'],
      submenu: [
        { label: 'Overview', path: '/ess/portal', icon: Home },
        { label: 'My Profile', path: '/employee-profile', icon: User },
        { label: 'Statutory Forms', path: '/ess/statutory-forms', icon: ShieldCheck },
        { label: 'Leave & Attendance', path: '/ess/leave-attendance', icon: Calendar },
        { label: 'Pay & Tax', path: '/ess/pay-tax', icon: DollarSign },
        // Stage 73.20cw — Expense Claims group (3 modules).
        // Reimbursements + Loans moved out of "Pay, Tax & Claims" per PRD;
        // same page/APIs/data/workflow — only the menu location changed.
        { separator: true, path: '__sep_expense_claims__', label: '── Expense Claims' },
        { label: 'Expense Claim Sheets', path: '/ess/expense-claims', icon: Receipt },
        { label: 'Reimbursements',       path: '/ess/reimbursements',  icon: Receipt },
        { label: 'Advance',              path: '/ess/loans',           icon: Wallet },
        { label: 'My Appointment Order', path: '/ess/appointment-order', icon: FileCheck },
        { label: 'KYC Confirmation', path: '/ess/kyc', icon: ShieldCheck },
        { label: 'Documents & Support', path: '/ess/documents', icon: HelpCircle },
        { label: 'My Assets & Docs', path: '/ess/assets-docs', icon: Package },
        { label: 'Help Desk', path: '/ess/helpdesk', icon: LifeBuoy },
        { label: 'User Guide', path: '/ess/user-guide', icon: BookOpen },
        { label: 'Advance & Claim Balance', path: '/ess/advance-ledger', icon: Wallet },
        { label: 'My Travel Requests', path: '/ess/travel-requests', icon: Plane },
      ] },
    { label: 'Travel Desk', icon: Plane, path: '/travel',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin', 'finance_reviewer', 'department_manager', 'employee'],
      submenu: [
        { label: 'Dashboard', path: '/travel/dashboard', icon: LayoutDashboard },
        { label: 'New Travel Request', path: '/travel/request/new', icon: FileText },
        { label: 'Travel Requests', path: '/travel/requests', icon: FileText },
        { label: 'Approval Inbox', path: '/travel/approval', icon: CheckSquare },
        { label: 'Booking Desk', path: '/travel/booking', icon: Receipt },
        { label: 'Cab Booking', path: '/travel/cab-booking', icon: MapPin },
        { label: 'Hotel Booking', path: '/travel/hotel-booking', icon: MapPin },
        { label: 'Travel Policy Master', path: '/travel/policy', icon: BookOpen },
        { label: 'Approval Policy Mapping', path: '/travel/policy-mapping', icon: ShieldCheck },
        { label: 'Travel Budgets', path: '/travel/budget', icon: Wallet },
        { label: 'Company Credit Cards', path: '/travel/credit-cards', icon: CreditCard },
        { label: 'Vendor Management', path: '/travel/vendors', icon: Building2 },
        { label: 'Travel Reports', path: '/travel/reports', icon: FileBarChart2 },
        { label: 'Settings & Calendar', path: '/travel/settings', icon: CalendarDays },
      ] },
    { label: 'Manager', icon: UsersRound, path: '/manager',
      // Stage 73.20dg — 'is_reporting_manager' is a virtual role activated
      // when the user is mapped as RM for ≥1 active employee. See
      // passesLegacyRoleCheck below where we treat it as auto-allowed.
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin', 'department_manager', 'is_reporting_manager'],
      submenu: [
        { label: 'Team Dashboard', path: '/manager/dashboard', icon: LayoutDashboard },
        { label: 'Approvals', path: '/manager/approvals', icon: CheckSquare },
        { label: 'My Line-Item Inbox', path: '/line-item-approval/inbox', icon: ClipboardCheck },
      ] },
    { label: 'HR Workbench', icon: Inbox, path: '/hr-workbench',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin'] },
    { label: 'HR Bench (Input Control)', icon: ListChecks, path: '/hr-bench',
      roles: ['tenant_admin', 'hr_admin', 'hr_manager', 'payroll_admin', 'finance_reviewer'] },
    { label: 'HR Bench Reports', icon: ListChecks, path: '/hr-bench/reports',
      roles: ['tenant_admin', 'hr_admin', 'hr_manager', 'payroll_admin', 'finance_reviewer'] },
    { label: 'Document Templates', icon: Layers, path: '/hr/document-templates',
      roles: ['tenant_admin', 'hr_admin', 'hr_manager', 'payroll_admin'] },
    { label: 'Appointment Orders', icon: FileCheck, path: '/hr/appointment-orders',
      roles: ['tenant_admin', 'hr_admin', 'hr_manager', 'payroll_admin'] },
    { label: 'Increment Workbench', icon: TrendingUp, path: '/hr/increment-workbench',
      roles: ['tenant_admin', 'hr_admin', 'hr_manager', 'payroll_admin', 'department_manager'] },
    { label: 'Advance Settlements', icon: Wallet, path: '/advance-settlements',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin', 'finance_reviewer'] },
    { label: 'Analytics', icon: BarChart3, path: '/analytics',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin', 'finance_reviewer'] },
    { label: 'Reports', icon: FileBarChart2, path: '/reports',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin', 'finance_reviewer'],
      submenu: [
        { label: 'Reports Center', path: '/reports', icon: FileBarChart2 },
        { label: 'TDS Validation', path: '/reports/tds-validation', icon: FileBarChart2 },
        { label: 'Approval Reports', path: '/reports/approvals', icon: FileBarChart2 },
      ] },
    { label: 'Finance', icon: Landmark, path: '/finance',
      roles: ['tenant_admin', 'payroll_admin', 'hr_admin', 'finance_reviewer'],
      submenu: [
        { label: 'Finance Overview', path: '/finance', icon: Landmark },
        { label: 'Finance Payments', path: '/finance/payments', icon: CreditCard },
        { label: 'Advance Ledger', path: '/finance/advance-ledger', icon: Wallet },
      ] },
    { label: 'Business Controls', icon: Shield, path: '/business-controls',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin'] },
    { label: 'Employees', icon: UsersRound, path: '/employees',
      roles: ['tenant_admin', 'hr_admin'],
      submenu: [
        { label: 'Employees List', path: '/employees', icon: UsersRound },
        { label: 'ESS Approvals', path: '/hr/ess-approvals', icon: CheckSquare },
        { label: 'KYC Cycles', path: '/payroll/kyc-cycles', icon: ShieldCheck },
        { label: 'KYC Corrections', path: '/payroll/kyc-corrections', icon: CheckSquare },
      ] },
    { label: 'Leave', icon: TreePalm, path: '/leave',
      roles: ['tenant_admin', 'hr_admin', 'department_manager'],
      submenu: [
        { label: 'Leave Management', path: '/leave', icon: TreePalm },
        { label: 'Leave Policy', path: '/leave-policy', icon: BookOpen },
        { label: 'Leave Balances', path: '/leave-policy/balances', icon: Wallet },
      ] },
    { label: 'Attendance', icon: Clock, path: '/attendance',
      roles: ['tenant_admin', 'hr_admin', 'attendance_admin', 'department_manager', 'payroll_admin', 'finance_reviewer'],
      submenu: [
        { label: 'Dashboard', path: '/attendance', icon: LayoutDashboard },
        { label: '── Operations', path: '#sep1', icon: Clock, separator: true },
        { label: 'Upload / Import', path: '/attendance/upload', icon: FileText },
        { label: 'Upload History', path: '/attendance/history', icon: BookOpen },
        { label: 'Internal Attendance', path: '/attendance/internal', icon: ClipboardCheck },
        { label: 'Calculation', path: '/attendance/calculation', icon: Calculator },
        { label: 'Reports', path: '/attendance/reports', icon: FileBarChart2 },
        { label: '── Daily View', path: '#sep2', icon: Clock, separator: true },
        { label: 'Daily Register', path: '/attendance/daily', icon: CalendarDays },
        { label: 'Monthly Summary', path: '/attendance/monthly', icon: Calendar },
        { label: 'Punch Logs', path: '/attendance/punches', icon: Clock },
        { label: '── Configuration', path: '#sep3', icon: Clock, separator: true },
        { label: 'Biometric Devices', path: '/attendance/biometric', icon: Shield },
        { label: 'Device Mapping', path: '/attendance/biometric/mapping', icon: UserCheck },
        { label: 'eTimeTrackLite Import', path: '/attendance/biometric/import', icon: FileText },
        { label: 'Face Attendance', path: '/attendance/face-logs', icon: UserCheck },
        { label: 'Face Punch (Self)', path: '/attendance/face-punch', icon: ScanFace },
        { label: 'Geo-fence Setup', path: '/attendance/geofence', icon: Database },
        { label: 'Shift Roster', path: '/attendance/roster', icon: ArrowLeftRight },
        { label: '── Approvals', path: '#sep4', icon: Clock, separator: true },
        { label: 'Regularization', path: '/attendance/regularization', icon: FileSearch },
        { label: 'OT Approval', path: '/attendance/ot-approval', icon: Calculator },
        { label: 'Exception Register', path: '/attendance/exception-register', icon: AlertTriangle },
      ] },
    { label: 'Payroll', icon: Wallet, path: '/payroll',
      roles: ['tenant_admin', 'payroll_admin', 'hr_admin', 'finance_reviewer'],
      submenu: [
        { label: '── Setup & Masters', path: '#sep-pay1', icon: Wallet, separator: true },
        { label: 'Payroll Setup', path: '/payroll/setup', icon: CalendarDays },
        { label: 'Wage Master', path: '/payroll/wage-master', icon: Coins },
        { label: 'Structure Overview', path: '/payroll/structures', icon: Receipt },
        { label: 'CTC Planner', path: '/payroll/ctc-planner', icon: Calculator },
        { label: 'Employee CTC Upload', path: '/payroll/employee-ctc-upload', icon: FileText },
        { label: 'Legacy / Historical Import', path: '/payroll/legacy-imports', icon: History },
        { label: '── Inputs (per cycle)', path: '#sep-pay2', icon: Wallet, separator: true },
        { label: 'Monthly Inputs', path: '/monthly-inputs', icon: ClipboardList },
        { label: 'Arrears', path: '/payroll/arrears', icon: FileText },
        { label: 'Advances', path: '/payroll/loans', icon: CreditCard },
        { label: 'Advance Policy', path: '/payroll/loan-policy', icon: ShieldCheck },
        { label: 'Reimbursements Tracker', path: '/payroll/reimbursements-tracker', icon: Receipt },
        { label: 'Reimbursement Policy', path: '/payroll/reimbursement-policy', icon: ShieldCheck },
        { label: 'Expense Sheets Review', path: '/payroll/expense-claims-admin', icon: Receipt },
        { label: 'Approval Workflows', path: '/payroll/approval-workflows', icon: ShieldCheck },
        { label: 'Salary Revision', path: '/payroll/salary-revision', icon: TrendingUp },
        { label: '── Execution', path: '#sep-pay3', icon: Wallet, separator: true },
        // Stage 73.20cr — Consolidated Monthly Payroll Input Approval gates
        // the Monthly Run. Placed at the top of the Execution group so HR
        // sees it BEFORE clicking Monthly Run.
        { label: 'Consolidated Approval', path: '/consolidated-approval', icon: ClipboardCheck },
        // Universal Approval Engine (dynamic multi-level approval chains)
        { label: 'Approval Dashboard', path: '/line-item-approval/dashboard', icon: ClipboardCheck },
        { label: 'My Approval Inbox', path: '/line-item-approval/inbox', icon: ClipboardCheck },
        { label: 'Approval Rules', path: '/line-item-approval/workflows', icon: Wrench },
        { label: 'Monthly Run', path: '/payroll/run', icon: Banknote },
        { label: 'Exceptions', path: '/payroll/exceptions', icon: AlertTriangle },
        { label: 'Approval', path: '/payroll/approval', icon: ClipboardCheck },
        { label: '── Output', path: '#sep-pay4', icon: Wallet, separator: true },
        { label: 'Payslips', path: '/payroll/payslips', icon: FileBarChart },
        { label: 'Tax Sheets', path: '/payroll/tax-sheets', icon: FileText },
        { label: 'F&F Settlement', path: '/payroll/fnf', icon: ArrowLeftRight },
      ] },
    { label: 'Statutory', icon: ShieldCheck, path: '/statutory',
      roles: ['tenant_admin', 'payroll_admin', 'hr_admin', 'finance_reviewer'],
      submenu: [
        { label: '── Setup', path: '#sep-stat1', icon: ShieldCheck, separator: true },
        { label: 'Setup Master', path: '/statutory/setup', icon: Wrench },
        { label: 'State-wise PT & LWF', path: '/statutory/state-rates', icon: MapPin },
        // Stage 73.20cp — Dynamic State/District statutory masters (PT/LWF rules)
        { label: 'Statutory Masters', path: '/statutory/masters', icon: Wrench },
        { label: 'Employee Eligibility', path: '/statutory/eligibility', icon: UserCheck },
        { label: '── Declarations', path: '#sep-stat2', icon: ShieldCheck, separator: true },
        { label: 'Declarations & Proofs', path: '/statutory/declarations', icon: FileSearch },
        { label: 'TDS Projection', path: '/statutory/tds-projection', icon: TrendingUp },
        { label: '── Working', path: '#sep-stat3', icon: ShieldCheck, separator: true },
        { label: 'PF/ESI/PT/LWF Working', path: '/statutory/working', icon: Calculator },
        { label: 'Challan Preparation', path: '/statutory/challans', icon: FileSignature },
        { label: '── Filings & Registers', path: '#sep-stat4', icon: ShieldCheck, separator: true },
        { label: 'Monthly Register', path: '/statutory/register', icon: BookOpen },
        { label: 'Annual Tax Worksheet', path: '/statutory/tax-worksheet', icon: FileBarChart },
        { label: '── Compliance', path: '#sep-stat5', icon: ShieldCheck, separator: true },
        { label: 'Compliance Dashboard', path: '/statutory/compliance', icon: Gauge },
      ] },
    { label: 'Master Setup', icon: Sparkles, path: '/masters/setup',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin', 'attendance_admin'] },
    { label: 'Asset & Doc Types', icon: Package, path: '/masters/asset-document-types',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin'] },
    { label: 'Tax Slab Master', icon: Layers, path: '/masters/tax-slabs',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin'] },
    { label: 'Help & Guide', icon: BookOpen, path: '/admin/processing-guide',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin', 'attendance_admin', 'finance_reviewer'] },
    { label: 'Companies', icon: Building2, path: '/companies',
      roles: ['tenant_admin', 'hr_admin'] },
    { label: 'Organization', icon: Network, path: '/organization',
      roles: ['tenant_admin', 'hr_admin'] },
    { label: 'Users', icon: Users, path: '/users',
      roles: ['tenant_admin', 'hr_admin'] },
    { label: 'Roles', icon: Shield, path: '/roles',
      roles: ['tenant_admin'] },
    { label: 'Audit Logs', icon: FileText, path: '/audit-logs',
      roles: ['tenant_admin', 'hr_admin', 'finance_reviewer'] },
    { label: 'Bulk Data Reset', icon: Trash2, path: '/admin/data-reset',
      roles: ['tenant_admin', 'hr_admin'] },
    { label: 'Notifications', icon: Bell, path: '/notifications',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin', 'attendance_admin', 'finance_reviewer', 'department_manager', 'employee'] },
    { label: 'Action Inbox', icon: CheckSquare, path: '/action-inbox',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin', 'finance_reviewer', 'department_manager'] },
    { label: 'Profile', icon: User, path: '/profile',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin', 'attendance_admin', 'finance_reviewer', 'department_manager', 'employee'] },
  ];

  const menuItems = isSuperAdmin
    ? platformMenu.map(m => ({ ...m, roles: ['super_admin'] }))
    : tenantMenu;

  // Tenant-level module enforcement: hide menu items whose module is disabled
  // for this tenant (controlled via Super Admin's Module Control page).
  const moduleKeyByLabel = {
    Payroll: 'payroll', Attendance: 'attendance', Leave: 'leave',
    Statutory: 'statutory', Employees: 'employees', Reports: 'reports',
    Finance: 'finance', Analytics: 'analytics', 'Travel Desk': 'travel_desk',
  };
  const enabledModules = Array.isArray(user?.enabled_modules) ? user.enabled_modules : null;
  const isModuleVisible = (label) => {
    if (isSuperAdmin) return true;
    const key = moduleKeyByLabel[label];
    if (!key) return true; // non-gated menu (Dashboard, Profile, etc.)
    if (!enabledModules) return true; // backward compatible
    return enabledModules.includes(key);
  };

  // When user.permissions is supplied, the permission matrix is the source of
  // truth. We still respect the legacy `roles: [...]` arrays as a backstop
  // for built-in roles. Custom-role users (role keys starting with `custom:`)
  // ALWAYS defer to the permission matrix — they never bypass filtering,
  // otherwise a fresh custom role with zero permissions would render the
  // full sidebar (Stage 73.20ek bug fix).
  const hasPerms = user && Array.isArray(user.permissions) && user.permissions.length > 0;
  const isCustomRole = (user?.role || '').startsWith('custom:');
  const passesLegacyRoleCheck = (item) => {
    if (isReportingManager && (item.roles || []).includes('is_reporting_manager')) return true;
    if (isCustomRole) {
      return true;
    }
    const userRole = user?.role;
    const userRoles = Array.isArray(user?.roles) ? user.roles : [userRole];
    const isCompanyAdmin = userRole === 'COMPANY_ADMIN' || userRole === 'company_admin' || userRoles.includes('COMPANY_ADMIN') || userRoles.includes('company_admin') || userRole === 'tenant_admin' || userRoles.includes('tenant_admin');

    if (!item.roles || item.roles.length === 0) return true;
    if (isCompanyAdmin && item.roles.includes('tenant_admin')) return true;

    return item.roles.some(r => userRoles.includes(r) || (isCompanyAdmin && r === 'tenant_admin'));
  };

  // Stage 73.20dg — View Switcher.
  //   activeView='manager' → show only Dashboard + Manager submenu + Line-Item Inbox
  //   activeView='employee' → hide the Manager top-level menu but keep everything else.
  const viewGate = (item) => {
    if (isSuperAdmin) return true;                                // super admin unaffected
    if (!isReportingManager) return true;                         // no toggle → show everything
    if (activeView === 'manager') {
      // Keep Dashboard, Manager, HR Workbench (if role allows), and
      // shared Notifications/Profile-style items. Hide the ESS "My Portal"
      // and all payroll/HR modules to focus on approvals.
      const managerModeAllowed = new Set([
        'Dashboard', 'Manager', 'HR Workbench',
      ]);
      return managerModeAllowed.has(item.label);
    }
    // Employee view — hide the Manager top-level entry.
    return item.label !== 'Manager';
  };

  const filteredMenu = menuItems
    .filter((item) => passesLegacyRoleCheck(item) && isModuleVisible(item.label) && viewGate(item))
    .map((item) => {
      // Apply permission-matrix filtering.
      // Stage 73.20ek — Custom-role users MUST always be filtered by the
      // permission matrix. Previously, an unpermissioned custom role fell
      // through the `!hasPerms` early-exit and saw the entire sidebar.
      if (user.role === 'super_admin') return item;
      const enforceMatrix = isCustomRole || hasPerms;
      if (!enforceMatrix) return item; // legacy built-in role, session with no permissions loaded → keep old behaviour

      // Leaf item (no submenu): visibility depends solely on whether the
      // caller has the required permission. `canViewPath` returns true for
      // any path missing from the map, but leaf sidebar entries are always
      // mapped, so this is safe.
      if (!item.submenu) {
        return canViewPath(user, item.path) ? item : null;
      }

      // Parent item with submenu: compute the visible NON-SEPARATOR
      // children first. If ZERO real children are visible, drop the entire
      // parent — this is what fixes the "dashboard shows every parent"
      // bug for custom roles. Previously the parent path (e.g. `/payroll`)
      // wasn't in PATH_TO_PERMISSION → canViewPath(parent) returned true
      // → parent kept even with no visible children.
      const realVisible = item.submenu.filter(
        (sub) => !sub.separator && canViewPath(user, sub.path)
      );
      if (realVisible.length === 0) return null;

      // Re-run the filter to also keep separators that surround visible
      // children (so section headers "── Operations" etc. don't get lost).
      const visibleSubs = item.submenu.filter(
        (sub) => sub.separator || canViewPath(user, sub.path)
      );
      // Drop trailing/leading separators and consecutive separators.
      const cleaned = [];
      let pendingSep = null;
      for (const sub of visibleSubs) {
        if (sub.separator) {
          pendingSep = sub;
        } else {
          if (pendingSep) { cleaned.push(pendingSep); pendingSep = null; }
          cleaned.push(sub);
        }
      }
      return { ...item, submenu: cleaned };
    })
    .filter(Boolean);

  // Data-driven auto-expand: on the first render for a new URL, find the
  // parent whose submenu contains the current pathname and expand it. We key
  // ONLY on `location.pathname` so manual clicks (via handleExpand) are not
  // overridden by this effect firing on every render. `filteredMenu` is a
  // fresh array each render — including it as a dep caused the effect to
  // fire constantly and silently undo every user expand/collapse, which is
  // why clicking "Payroll" while on an Attendance page felt like the sidebar
  // "got stuck" on Attendance.
  const menuRef = React.useRef();
  menuRef.current = filteredMenu;
  React.useEffect(() => {
    const match = (menuRef.current || []).find(
      (item) =>
        item.submenu &&
        (item.submenu.some((s) => s.path === location.pathname) ||
          (item.path === '/travel' && location.pathname.startsWith('/travel')))
    );
    if (match) {
      setExpandedMenu(match.label);
      sessionStorage.setItem('sidebar-expanded', match.label);
    }
  }, [location.pathname]);

  const isActive = (path) => {
    if (location.pathname === path) return true;
    if (
      path === '/travel/requests' &&
      location.pathname.startsWith('/travel/request/') &&
      location.pathname !== '/travel/request/new'
    ) {
      return true;
    }
    return false;
  };

  const isParentActive = (item) => {
    if (!item.submenu) return false;
    if (item.path === '/travel' && location.pathname.startsWith('/travel')) return true;
    return item.submenu.some((s) => location.pathname === s.path);
  };

  // ---- Gold Palette (rich, vibrant, metallic) ----
  // #D4AF37 = antique museum gold (deep base)
  // #FFC000 = primary vibrant gold (deeper saturation)
  // #FFE76A = highlight gold (bright)
  // #FFF4C2 = champagne (top sheen)
  const goldText = 'linear-gradient(135deg, #D4AF37 0%, #FFC000 35%, #FFE76A 70%, #FFF4C2 100%)';
  const goldTextHover = 'linear-gradient(135deg, #FFC000 0%, #FFE76A 50%, #FFF4C2 100%)';
  const goldTextActive = 'linear-gradient(135deg, #FFE76A 0%, #FFF4C2 50%, #FFE76A 100%)';
  const shimmerStyle = (gradient, glow) => ({
    backgroundImage: gradient,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    filter: glow ? 'drop-shadow(0 0 6px rgba(255, 192, 0, 0.55))' : 'drop-shadow(0 0 2px rgba(212, 175, 55, 0.3))',
  });

  return (
    <>
      {/* Mobile Backdrop overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 md:hidden transition-opacity duration-300"
          onClick={onClose}
        />
      )}

      <div
        className={`fixed left-0 top-0 h-screen text-white flex flex-col z-40 transition-all duration-300 ease-in-out overflow-hidden
          ${isOpen ? 'translate-x-0 w-[280px]' : '-translate-x-full w-[280px] md:translate-x-0 md:w-20 lg:w-[280px]'}`}
        style={{ background: 'var(--brand-secondary, #0F172A)' }}
      >
        {/* Fixed Header */}
        <div className="p-6 md:p-4 lg:p-6 border-b flex items-center justify-between shrink-0 w-full" style={{ borderColor: 'rgba(255, 192, 0, 0.22)' }}>
          <div className="flex items-center space-x-3 md:space-x-0 lg:space-x-3 w-full md:justify-center lg:justify-start">
            <img
              src={branding.logo_url || "https://static.prod-images.emergentagent.com/jobs/eddffb21-269e-422d-8d01-986a48fb2bf0/images/93e6f472f053496c87144ac581b8492d02489b4d2eba4a5016d8a67bde75008c.png"}
              alt="Logo"
              className="w-10 h-10 object-contain"
            />
            <div className={`md:hidden lg:block ${isOpen ? 'block' : ''}`}>
              <h1
                className="text-lg font-bold tracking-tight whitespace-nowrap"
                style={{
                  color: '#FFE76A',
                  textShadow: '0 0 12px rgba(255, 192, 0, 0.45), 0 1px 2px rgba(0, 0, 0, 0.6)',
                  letterSpacing: '0.02em',
                }}
              >
                {branding.app_name || 'PayrollPro'}
              </h1>
              <p className="text-xs font-medium" style={{ color: '#FFC000', opacity: 0.85 }}>
                Enterprise Suite
              </p>
            </div>
          </div>
          {isOpen && (
            <button onClick={onClose} className="md:hidden text-slate-400 hover:text-white p-1">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Scrollable Navigation */}
        <nav ref={navRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-1 scrollbar-thin w-full">
          {filteredMenu.map((item) => {
            if (item.submenu) {
              const isExpanded = expandedMenu === item.label;
              const parentActive = isParentActive(item);
              return (
                <SidebarGroup
                  key={item.label}
                  label={item.label}
                  icon={item.icon}
                  parentActive={parentActive}
                  isExpanded={isExpanded}
                  onExpandClick={() => handleExpand(item.label)}
                  shimmerStyle={shimmerStyle}
                  goldTextActive={goldTextActive}
                  goldText={goldText}
                  isOpen={isOpen}
                >
                  {item.submenu.map((sub) => {
                    if (sub.separator) {
                      return (
                        <SidebarSection
                          key={sub.path}
                          label={sub.label}
                          isOpen={isOpen}
                        />
                      );
                    }
                    return (
                      <SidebarItem
                        key={sub.path}
                        to={sub.path}
                        label={sub.label}
                        icon={sub.icon}
                        active={isActive(sub.path)}
                        onClick={onClose}
                        isChild={true}
                        shimmerStyle={shimmerStyle}
                        goldTextActive={goldTextActive}
                        goldText={goldText}
                        isOpen={isOpen}
                      />
                    );
                  })}
                </SidebarGroup>
              );
            }
            return (
              <SidebarItem
                key={item.path}
                to={item.path}
                label={item.label}
                icon={item.icon}
                active={isActive(item.path)}
                onClick={onClose}
                isChild={false}
                shimmerStyle={shimmerStyle}
                goldTextActive={goldTextActive}
                goldText={goldText}
                isOpen={isOpen}
              />
            );
          })}
        </nav>

        {/* Fixed Footer */}
        <SidebarFooter
          user={user}
          shimmerStyle={shimmerStyle}
          goldText={goldText}
          isOpen={isOpen}
          UserIcon={User}
        />
      </div>
    </>
  );
};

export default Sidebar;
